<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useSearchStore } from '@/stores/search.js';
import { useDebounceFn } from '@vueuse/core';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import InputText from 'primevue/inputtext';

const router = useRouter();
const route = useRoute();
const searchStore = useSearchStore();
const searchText = ref(searchStore.query);

// Утилиты
const isSearchRoute = (path) => typeof path === 'string' && path.replace(/\/+$/, '') === '/search';

// Полная синхронизация поля c query стора
watch(
    () => searchStore.query,
    (newVal) => {
      searchText.value = newVal;
    }
);

// Единое очищение инпута и стора
function clearAll() {
  searchText.value = '';
  if (typeof searchStore.clearResults === 'function') {
    searchStore.clearResults(); // сбрасывает query + результаты
  } else {
    searchStore.query = '';
  }
}

// Реакция на внешнее событие (генерируется страницей результатов поиска при пагинации и т.п.)
function handleExternalClear() {
  clearAll();
}

onMounted(() => {
  window.addEventListener('global-search:clear-input', handleExternalClear);
});
onBeforeUnmount(() => {
  window.removeEventListener('global-search:clear-input', handleExternalClear);
});

// Ключевое: очищаем поле при любом уходе с страницы результатов
watch(
    () => route.fullPath,
    (newPath, oldPath) => {
      if (isSearchRoute(oldPath) && !isSearchRoute(newPath)) {
        clearAll();
      }
    }
);

const executeSearch = () => {
  searchStore.query = (searchText.value || '').trim();

  if (!isSearchRoute(route.fullPath)) {
    router.push('/search');
  }
  // метод-алиас в сторе
  searchStore.performSearch();
};

const debouncedSearch = useDebounceFn(() => {
  executeSearch();
}, 500);

// При вводе — обновляем стор; если уже на /search, запускаем дебаунс-поиск/очистку
watch(searchText, (newQuery) => {
  searchStore.query = newQuery;
  if (isSearchRoute(route.fullPath)) {
    if (newQuery.trim().length >= 3) {
      debouncedSearch();
    } else if (newQuery.trim().length === 0) {
      clearAll();
    }
  }
});
</script>

<template>
  <IconField class="pt-2">
    <InputIcon class="pi pi-search pt-1" />
    <InputText
        v-model="searchText"
        placeholder="Поиск..."
        size="small"
        class="min-w-96"
        @keyup.enter="executeSearch"
    />
  </IconField>
</template>